import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { handleErrors } from '../../../../../utils/functions';
import { showAlert } from '../../../../../redux/slices/appSlice';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetAllDocumentTypesQuery } from '../../../../../services/documentsApi';
import {
    useAddRolMutation,
    useGetRoleByIdQuery,
    useUpdateRolMutation
} from '../../../../../services/adminApi';

type PermissionProps = {
    tipoDocumento: any;
    cargar: boolean;
    descargar: boolean;
    // modificar: boolean;
    eliminar: boolean;
};

export interface Data {
    id?: number;
    permisos: PermissionProps[];
}

const defaultValues = {
    nombre: '',
    descripcion: '',
    permisos: []
};

function useRolesSave({ selectedItem, handleCloseDrawer, drawerAction }: any) {
    const dispatch = useAppDispatch();
    const [roleName, setRoleName] = useState<string>('');
    const [roleDescription, setRoleDescription] = useState<string>('');
    const [data, setData] = useState<Data>(selectedItem || defaultValues);
    const scrollRef = useRef<null | HTMLDivElement>(null);
    const [auxData, setAuxData] = useState<any>(null);
    const [addRole, { isLoading: addingRole }] = useAddRolMutation();
    const [updateRole, { isLoading: updatingRole }] = useUpdateRolMutation();
    const {
        data: roleById,
        isLoading: loadingRoleById,
        isFetching: fetchingRoleById
    } = useGetRoleByIdQuery(selectedItem?.id, {
        refetchOnMountOrArgChange: true,
        skip: !selectedItem?.id || !['new', 'edit'].includes(drawerAction)
    });

    const {
        data: allDocumentTypes,
        isLoading: loadingAllDocumentTypes,
        isFetching: fetchingAllDocumentTypes
    } = useGetAllDocumentTypesQuery(
        {},
        {
            refetchOnMountOrArgChange: false,
            skip:
                (drawerAction === 'new' && (!roleName || !roleDescription)) ||
                !['new', 'edit'].includes(drawerAction)
        }
    );

    useEffect(() => {
        if (!drawerAction) setData(defaultValues);
    }, [drawerAction]);

    const getPermissions = (permissions: any) => {
        return permissions?.map((permission: any) => ({
            id: Number(permission.tipoDocumento.id),
            cargar: permission.cargar,
            descargar: permission.descargar,
            eliminar: permission.eliminar,
            tipoDocumento: allDocumentTypes?.find(
                (ed: any) => ed.tipoDocumento.id === Number(permission.tipoDocumento.id)
            )?.tipoDocumento
        }));
    };

    useEffect(() => {
        if (roleById && drawerAction === 'edit' && !!allDocumentTypes?.length) {
            loadData();
        }
        // eslint-disable-next-line
    }, [roleById, drawerAction, allDocumentTypes]);

    const loadData = async () => {
        const values = { ...roleById, permisos: getPermissions(roleById?.permisos) };
        setRoleName(roleById?.nombre);
        setRoleDescription(roleById?.descripcion);
        setData(values);
        setAuxData(values);
    };

    const clearData = () => {
        setRoleName('');
        setRoleDescription('');
        setData({
            permisos: []
        });
        if (auxData) setAuxData(null);
    };

    const handleChangeData = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const { name, value } = e.target;
            setData((prevData: any) => ({
                ...prevData,
                [name]: value || null
            }));
        },
        // eslint-disable-next-line
        [drawerAction]
    );

    const scrollView = () => {
        setTimeout(() => {
            return scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleSubmit = async () => {
        if (drawerAction === 'edit') {
            if (
                _.isEqual(
                    { nombre: roleName, descripcion: roleDescription },
                    {
                        nombre: auxData?.nombre,
                        descripcion: auxData?.descripcion
                    }
                )
            ) {
                clearData();
                handleCloseDrawer();
            } else {
                const handleUpdateRole = async () => {
                    const body = {
                        nombre: roleName,
                        descripcion: roleDescription
                    };
                    const response: any = await updateRole({ rolId: auxData?.id, body });
                    if (!response.error) {
                        return enqueueSnackbar('Datos actualizado con éxito.', {
                            variant: 'success'
                        });
                    } else {
                        handleErrors(response.error);
                    }
                };
                dispatch(
                    showAlert({
                        title: '¿Actualizar datos de rol?',
                        keepMounted: true,
                        cancelText: 'Cancelar',
                        confirmText: 'Sí, actualizar',
                        confirmAction: handleUpdateRole
                    })
                );
            }
        } else {
            const submit = async () => {
                const body: any = {
                    nombre: roleName,
                    descripcion: roleDescription,
                    permisos: data?.permisos?.map((item: any) => ({
                        idTipoDocumento: item?.tipoDocumento?.id,
                        cargar: item?.cargar,
                        descargar: item?.descargar,
                        // modificar: false,
                        eliminar: item?.eliminar
                    }))
                };
                const response: any = await addRole(body);
                if (!response.error) {
                    clearData();
                    handleCloseDrawer();
                    enqueueSnackbar(response?.data?.message || 'Rol creado con éxito.', {
                        variant: 'success'
                    });
                } else {
                    handleErrors(response?.error);
                }
            };
            dispatch(
                showAlert({
                    title: '¿Crear nuevo rol?',
                    keepMounted: true,
                    cancelText: 'Cancelar',
                    confirmText: 'Sí, confirmar',
                    confirmAction: submit
                })
            );
        }
    };

    const formValidation = useMemo(() => {
        return (
            _.isEqual(
                { nombre: auxData?.nombre, descripcion: auxData?.descripcion },
                { nombre: roleName, descripcion: roleDescription }
            ) ||
            (drawerAction === 'new' && (!data || data?.permisos?.length === 0))
        );
    }, [auxData, roleName, roleDescription, data, drawerAction]);

    const loadingRoleData = useMemo(() => {
        return (
            loadingRoleById ||
            fetchingRoleById ||
            (drawerAction === 'edit' && (loadingAllDocumentTypes || fetchingAllDocumentTypes))
        );
    }, [
        loadingRoleById,
        fetchingRoleById,
        loadingAllDocumentTypes,
        fetchingAllDocumentTypes,
        drawerAction
    ]);

    return {
        data,
        roleName,
        roleDescription,
        auxData,
        allDocumentTypes,
        formValidation,
        loadingAllDocumentTypes,
        fetchingAllDocumentTypes,
        addingRole,
        updatingRole,
        loadingRoleById,
        fetchingRoleById,
        loadingRoleData,
        scrollRef,
        setRoleName,
        scrollView,
        setRoleDescription,
        handleSubmit,
        handleChangeData,
        clearData
    };
}

export default useRolesSave;
